/**
 * Utilitários para geração de nomes de arquivos e caminhos para pedidos
 */

import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { sendToTerminal, logger } from "../lib/terminalLogger";

/** Sufixo de contração variável para nomes de arquivo (espelho de OrderDetails/types/objects) */
const CONTRACTION_VARIABLE_FILE = "CV";
import { api } from "../lib/api";
import { GerArquivosService } from "../services/gerArquivosService";

interface PedidoInfo {
	orc_id?: number;
	oit_id?: number;
	cliente?: {
		id?: number;
		nome?: string;
		nome_fantasia?: string;
	};
	produto?: {
		nome?: string;
		tipo?: string | number;
	};
	material?: {
		nome?: string;
	};
	data_entrega?: string;
	nome_arquivo?: string;
	regras?: {
		diretorio_nome?: string;
		diretorio_base_interno?: string;
		diretorio_base_externo?: string;
		horario_corte?: string;
	};
}

interface DiretorioInfo {
	baseDir: string;
	tipoProduto?: string;
	periodo?: string;
}

// Pasta de mês padronizada: "NN-NOME" em MAIÚSCULAS
const MONTHS_PT_BR: string[] = [
	"01-JANEIRO",
	"02-FEVEREIRO",
	"03-MARÇO",
	"04-ABRIL",
	"05-MAIO",
	"06-JUNHO",
	"07-JULHO",
	"08-AGOSTO",
	"09-SETEMBRO",
	"10-OUTUBRO",
	"11-NOVEMBRO",
	"12-DEZEMBRO",
];

/**
 * Formata a data atual no padrão DD-MM (v3.0: SEM ano)
 * @returns String de data formatada
 */
export const getFormattedDate = (): string => {
	const now = new Date();
	const dd = format(now, "dd");
	const mm = format(now, "MM");
	return `${dd}-${mm}`;
};

/**
 * Formata o ano atual
 * @returns String do ano atual
 */
export const getCurrentYear = (): string => {
	return format(new Date(), "yyyy");
};

/**
 * Determina o período do dia (MANHÃ, TARDE, NOTURNA) com base no horário atual
 * Usa o formato 24h (00:00 às 23:59)
 * @returns String com o período do dia
 */
export const getPeriodOfDay = (): string => {
	const hours = new Date().getHours();
	let periodo: string;

	if (hours >= 6 && hours < 12) {
		periodo = "MANHA";
	} else if (hours >= 12 && hours < 18) {
		periodo = "TARDE";
	} else {
		periodo = "NOTURNA";
	}

	return periodo;
};

/**
 * Gera um hash de 4 dígitos aleatório para identificação única de arquivos
 * @returns String com 4 dígitos (0000-9999)
 *
 * @example
 * generateFourDigitHash() // "7842"
 * generateFourDigitHash() // "0012"
 * generateFourDigitHash() // "9999"
 */
export const generateFourDigitHash = (): string => {
	const hash = Math.floor(Math.random() * 10000);
	return String(hash).padStart(4, "0");
};

/**
 * Gera um nome de arquivo seguro para o sistema de arquivos no formato padrão
 * @param pedido Informações do pedido
 * @param qtdObjetos Quantidade de objetos no pedido
 * @param contraction Valor de contração usado ou "CV" para contrações variáveis
 * @param isUrgente Indica se o pedido é urgente
 * @returns Nome de arquivo sanitizado no formato padrão
 */
export const generateSafeFileName = (
	pedido: PedidoInfo,
	qtdObjetos?: number,
	contraction?: string,
	isUrgente?: boolean,
): string => {
	if (!pedido) {
		logger.warn(
			"⚠️ Dados do pedido não fornecidos para geração do nome do arquivo",
		);
		return sanitizeFileName("arquivo_sem_nome");
	}

	// Se não temos os dados básicos necessários, usar nome padrão
	if (!pedido.orc_id || !pedido.oit_id) {
		logger.warn(
			"⚠️ Dados básicos do pedido incompletos (orc_id ou oit_id ausentes)",
		);

		const dataFormatada = format(new Date(), "yyyyMMdd");
		const clienteName =
			pedido.cliente?.nome_fantasia || pedido.cliente?.nome || "Cliente";
		const produtoNome = pedido.produto?.nome || "Produto";

		// Nome padrão de fallback
		const fallbackName = `${dataFormatada}_${clienteName}_${produtoNome}${
			isUrgente ? "_URGENTE" : ""
		}`;
		return sanitizeFileName(fallbackName);
	}

	// Formato solicitado: orc_id_oit_id_nomeFantasia_Pts{qtdObjetos}_{contraction}.cdr
	// Se for urgente, adicionar _URGENTE antes da extensão

	const orc_id = pedido.orc_id;
	const oit_id = pedido.oit_id;
	// Sanitização especial do nome_fantasia: hífen em vez de underscore e sem acentos
	const sanitizeNomeFantasia = (input: string): string => {
		try {
			let s = String(input || "")
				.normalize("NFD")
				.replace(/[\u0300-\u036f]/g, "");
			s = s.replace(/[<>:"/\\|?*]/g, "-"); // inválidos → '-'
			s = s.replace(/[\s_]+/g, "-"); // espaços/underscores → '-'
			s = s.replace(/-+/g, "-"); // colapsar múltiplos '-'
			s = s.replace(/^-+|-+$/g, ""); // trim '-'
			return s || "Cliente";
		} catch {
			return "Cliente";
		}
	};

	const nomeFantasia = sanitizeNomeFantasia(
		pedido.cliente?.nome_fantasia || pedido.cliente?.nome || "Cliente",
	);
	const materialNome = pedido.material?.nome || "Material";
	const pontos =
		qtdObjetos !== undefined && qtdObjetos > 0 ? `pts${qtdObjetos}` : "pts1";

	// Determinar o valor de contração a usar no nome do arquivo
	let contraValor: string;

	// Verificar se a contração é variável (marcada como VAR ou VARIÁVEL)
	if (contraction === "VAR" || contraction === "VARIÁVEL") {
		// Usar CV para indicar contração variável
		contraValor = "CV";
	} else {
		// Usar o valor fornecido ou AUTO como padrão
		contraValor = contraction || "AUTO";
	}

	// Tag de urgência sem underscore
	const urgenteTag = isUrgente ? "URGENTE" : "";

	// Template atualizado com colchetes para identificadores:
	// [O-{orc_id}]_[I-{oit_id}]_{cliente}_{material}_pts{qtdObjetos}_{ctr}[_URGENTE]
	const fileName = `[O-${orc_id}]_[I-${oit_id}]_${nomeFantasia}_${materialNome}_${pontos}_${contraValor}${
		urgenteTag ? `_${urgenteTag}` : ""
	}`;

	const safeFileName = sanitizeFileName(fileName);
	return safeFileName;
};

/**
 * Sanitiza um nome de arquivo removendo caracteres inválidos
 * @param fileName Nome de arquivo original
 * @returns Nome de arquivo sanitizado
 */
// Sanitização de nome de arquivo - Importada do módulo centralizado de segurança
import { sanitizeFileName as sanitizeFileNameCore } from "../lib/security/sanitizers/filename.sanitizer";

/**
 * Sanitiza um nome de arquivo (wrapper para compatibilidade)
 *
 * @deprecated Use import direto de '@/lib/security/sanitizers/filename.sanitizer'
 * Esta função é mantida apenas para compatibilidade com código legado.
 */
export const sanitizeFileName = (fileName: string): string => {
	if (!fileName) {
		logger.warn("⚠️ Nome de arquivo vazio fornecido para sanitização");
		return "arquivo_sem_nome";
	}

	try {
		return sanitizeFileNameCore(fileName, "arquivo_sem_nome");
	} catch (error) {
		logger.error("❌ Erro ao sanitizar nome de arquivo:", error);
		return "arquivo_erro_sanitizacao";
	}
};

/**
 * Calcula a data de destino com base nas configurações de delivery e horário de corte
 * @param delivery Tipo de entrega ('Hoje', 'Amanhã', 'Noturna', 'URGENTE')
 * @param horarioCorteMm Horário de corte no formato HH:mm
 * @returns Objeto com data formatada, pasta noturna e ano
 */
export const calculateDestinationDate = (
	delivery?: string,
	horarioCorteMm?: string,
): {
	dataFormatada: string;
	subpastaNoturna: boolean;
	ano: string;
	mes: string;
	isUrgente: boolean;
} => {
	// Validar parâmetros de entrada
	const entregaValida = delivery || "Hoje";
	const horarioCorteValido = horarioCorteMm || "16:00";

	// Log removido - cálculo de data é operação rotineira

	const hoje = new Date();
	const amanha = new Date(hoje);
	amanha.setDate(amanha.getDate() + 1);

	// Determinar se já passou do horário de corte
	let [horaCorte, minutoCorte] = [15, 0]; // Valores padrão

	try {
		if (horarioCorteValido && horarioCorteValido.includes(":")) {
			[horaCorte, minutoCorte] = horarioCorteValido.split(":").map(Number);
		}
	} catch (error) {
		logger.warn(
			`⚠️ Formato de horário inválido: "${horarioCorteValido}". Usando padrão 15:00.`,
			error,
		);
	}

	const horarioCorte = new Date(hoje);
	horarioCorte.setHours(horaCorte, minutoCorte, 0);

	let dataAlvo: Date;
	let subpastaNoturna = false;
	let isUrgente = false;

	// Aplicar as regras conforme especificado:
	if (entregaValida === "URGENTE") {
		// URGENTE: sempre salvar para hoje, independente do horário de corte
		dataAlvo = hoje;
		isUrgente = true;
	} else if (entregaValida === "Hoje") {
		if (hoje < horarioCorte) {
			// Antes do horário de corte: salvar para hoje
			dataAlvo = hoje;
		} else {
			// Após o horário de corte: salvar para amanhã
			dataAlvo = amanha;
		}
	} else if (entregaValida === "Amanhã") {
		// Sempre salvar para amanhã
		dataAlvo = amanha;
	} else if (entregaValida === "Noturna") {
		// Salvar para hoje com subpasta NOTURNA
		dataAlvo = hoje;
		subpastaNoturna = true;
	} else {
		// Default: hoje
		dataAlvo = hoje;
		logger.warn(
			`⚠️ Tipo de entrega desconhecido: "${entregaValida}" - Usando padrão (hoje)`,
		);
	}

	// Formatar como DD-MM (SEM ano) - v3.0
	const dd = format(dataAlvo, "dd");
	const mm = format(dataAlvo, "MM");
	const dataFormatada = `${dd}-${mm}`;
	const ano = format(dataAlvo, "yyyy");
	const mes = MONTHS_PT_BR[dataAlvo.getMonth()]; // "NN-NOME" em MAIÚSCULAS

	// Log removido - resultado de cálculo de data é rotineiro

	return { dataFormatada, subpastaNoturna, ano, mes, isUrgente };
};

/**
 * Constrói o caminho completo do arquivo para usuários externos
 * @param pedido Informações do pedido
 * @param diretorioInfo Informações de diretório
 * @param qtdObjetos Quantidade de objetos no pedido
 * @param contraction Valor de contração usado
 * @param delivery Tipo de entrega
 * @param horarioCorte Horário de corte
 * @returns Caminho completo do arquivo
 */
export const buildExternalFilePath = (
	pedido: PedidoInfo,
	diretorioInfo: DiretorioInfo,
	qtdObjetos?: number,
	contraction?: string,
	delivery?: string,
	horarioCorte?: string,
): string => {
	// Log removido - construção de caminho é operação rotineira

	// Garantir que exista um caminho base
	if (!diretorioInfo.baseDir || diretorioInfo.baseDir.trim() === "") {
		logger.warn(
			"⚠️ Caminho base externo não definido! Usando caminho padrão do sistema.",
		);
	}

	// Informações base - Usar sempre o fornecido pela API
	const baseDir = diretorioInfo.baseDir;

	// Determinar data com base nas regras
	const { dataFormatada, subpastaNoturna, ano, mes, isUrgente } =
		calculateDestinationDate(delivery, horarioCorte);

	// Usar diretamente o tipoProduto fornecido em diretorioInfo
	const diretorioEspecifico = diretorioInfo.tipoProduto;

	if (!diretorioEspecifico) {
		logger.error("❌ ERRO: Tipo de diretório não definido!");
		logger.error(
			"   O arquivo será salvo na raiz do diretório de data, o que pode dificultar sua localização.",
		);
	}

	// Nome do arquivo com o formato especificado
	const fileName = generateSafeFileName(
		pedido,
		qtdObjetos,
		contraction,
		isUrgente,
	);

	// ✅ Estrutura atualizada: Base\LOCAL\Ano\@mês\Data\TipoProduto
	let caminho = `${baseDir}\\LOCAL\\${ano}\\${mes}\\${dataFormatada}`;

	// Adicionar o diretório específico apenas se ele existir
	if (diretorioEspecifico) {
		caminho += `\\${diretorioEspecifico}`;
	}

	// Adicionar NOTURNA se necessário
	if (subpastaNoturna) {
		caminho += "\\NOTURNA";
	}

	// Caminho final com nome do arquivo
	const caminhoFinal = `${caminho}\\${fileName}.CDR`;
	return caminhoFinal;
};

/**
 * Constrói o caminho completo do arquivo para usuários internos
 * @param pedido Informações do pedido
 * @param diretorioInfo Informações de diretório
 * @param qtdObjetos Quantidade de objetos no pedido
 * @param contraction Valor de contração usado
 * @param delivery Tipo de entrega
 * @param horarioCorte Horário de corte
 * @returns Caminho completo do arquivo
 */
export const buildInternalFilePath = (
	pedido: PedidoInfo,
	diretorioInfo: DiretorioInfo,
	qtdObjetos?: number,
	contraction?: string,
	delivery?: string,
	horarioCorte?: string,
): string => {
	// Log removido - construção de caminho é operação rotineira

	// Garantir que exista um caminho base
	if (!diretorioInfo.baseDir || diretorioInfo.baseDir.trim() === "") {
		logger.warn(
			"⚠️ Caminho base interno não definido! Usando caminho padrão do sistema.",
		);
	}

	// Usar sempre o caminho base fornecido pela API
	let baseDir = diretorioInfo.baseDir;

	// Verificar se o diretório base já tem um nome de arquivo no final
	if (baseDir && (baseDir.endsWith(".CDR") || baseDir.endsWith(".cdr"))) {
		baseDir = baseDir.substring(0, baseDir.lastIndexOf("\\"));
	}

	// Determinar data com base nas regras
	const { dataFormatada, subpastaNoturna, ano, mes, isUrgente } =
		calculateDestinationDate(delivery, horarioCorte);

	// Gerar o nome do arquivo no formato correto
	const fileName = generateSafeFileName(
		pedido,
		qtdObjetos,
		contraction,
		isUrgente,
	);

	// Usar diretamente o tipoProduto fornecido em diretorioInfo
	const diretorioEspecifico = diretorioInfo.tipoProduto;

	if (!diretorioEspecifico) {
		logger.error("❌ ERRO: Tipo de diretório não definido!");
		logger.error(
			"   O arquivo será salvo na raiz do diretório de data, o que pode dificultar sua localização.",
		);
	}

	// Construir estrutura do cliente: {cli_id}_{cli_nome_fantasia}
	const sanitize = (s?: string) =>
		(s || "")
			.normalize("NFD")
			.replace(/[\u0300-\u036f]/g, "")
			.replace(/[^a-zA-Z0-9\s]+/g, " ")
			.replace(/\s+/g, "_")
			.replace(/_+/g, "_")
			.replace(/^_|_$/g, "");
	// Tentar cli_id primeiro (da API), depois id como fallback
	const clienteId = (pedido.cliente as any)?.cli_id
		? String((pedido.cliente as any).cli_id)
		: pedido.cliente?.id
			? String(pedido.cliente.id)
			: "";
	const clienteNome = sanitize(
		pedido.cliente?.nome_fantasia || pedido.cliente?.nome || "",
	);
	const pastaCliente =
		clienteId && clienteNome ? `${clienteId}_${clienteNome}` : "";

	// ✅ Estrutura atualizada: Base\CLIENTES\{cli_id}_{cli_nome}\Ano\@mês\Data\TipoProduto\orcId\oitId
	let caminho = `${baseDir}\\CLIENTES`;

	// Adicionar pasta do cliente se existir
	if (pastaCliente) {
		caminho += `\\${pastaCliente}`;
	}

	caminho += `\\${ano}\\${mes}\\${dataFormatada}`;

	// Adicionar o diretório específico apenas se ele existir
	if (diretorioEspecifico) {
		caminho += `\\${diretorioEspecifico}`;
	}

	// Adicionar NOTURNA se necessário
	if (subpastaNoturna) {
		caminho += "\\NOTURNA";
	}

	// Incluir subpastas de identificação do pedido (orcId/oitId) conforme padrão padronizado
	// const orcIdFolder = String(pedido.orc_id ?? "0");
	// const oitIdFolder = String(pedido.oit_id ?? "0");
	// caminho += `\\${orcIdFolder}\\${oitIdFolder}`;

	// Caminho final com nome do arquivo
	const caminhoFinal = `${caminho}\\${fileName}.CDR`;
	return caminhoFinal;
};

/**
 * Determina o caminho de arquivo apropriado com base na feature flag de caminho interno.
 * @param useInternalFilePath - true = salva em CLIENTES\... (admin/vendedor/producao)
 *                              false = salva em LOCAL\... (revenda/cliente)
 *                              Controlado pela flag general.use_internal_file_path no banco.
 */
export function getAppropriateFilePath(
	useInternalFilePath: boolean,
	internalPath: string,
	externalPath: string,
	pedidoInfo: PedidoInfo,
	qtdObjetos: number,
	contraction: string,
	delivery: string,
	horarioCorte: string,
	diretorioNome?: string,
): string {
	// Validações obrigatórias - dados devem vir da API
	if (!diretorioNome || diretorioNome.trim() === "") {
		throw new Error(
			"diretorio_nome é obrigatório e deve vir das regras da API",
		);
	}

	if (!internalPath || internalPath.trim() === "") {
		throw new Error(
			"diretorio_base_interno é obrigatório e deve vir das regras da API",
		);
	}

	// Preparar a informação do diretório
	const internalDirInfo: DiretorioInfo = {
		baseDir: internalPath,
		tipoProduto: diretorioNome,
	};

	const externalDirInfo: DiretorioInfo = {
		baseDir: externalPath,
		tipoProduto: diretorioNome,
	};

	// Determinar o caminho do arquivo com base na feature flag
	let filePath: string;

	if (useInternalFilePath) {
		filePath = buildInternalFilePath(
			pedidoInfo,
			internalDirInfo,
			qtdObjetos,
			contraction,
			delivery,
			horarioCorte,
		);
	} else {
		// Validar que externos também têm caminho base
		if (!externalPath || externalPath.trim() === "") {
			throw new Error(
				"diretorio_base_externo é obrigatório para usuários externos",
			);
		}

		filePath = buildExternalFilePath(
			pedidoInfo,
			externalDirInfo,
			qtdObjetos,
			contraction,
			delivery,
			horarioCorte,
		);
	}
	return filePath;
}

/**
 * Gera nome de arquivo para exportação (PNG, PDF, TIFF, etc)
 *
 * DIFERENTE do generateSafeFileName (usado para CDR):
 * - Inclui ÍNDICE no início para ordenação em pastas
 * - Material aparece ANTES do cliente (agrupamento visual)
 * - Formato otimizado para múltiplos arquivos de exportação
 *
 * @param index Índice do arquivo na sequência (1, 2, 3...)
 * @param totalPartes Total de partes/objetos na exportação (equivalente ao pts{N} do CDR)
 * @param pedido Informações do pedido
 * @param multiplicador Quantidade de vezes que o objeto foi multiplicado
 * @param contraction Valor de contração aplicado
 * @returns Nome de arquivo formatado para exportação (sem extensão)
 *
 * @example
 * // Entrada: index=1, totalPartes=4, orc_id=12345, oit_id=67890, material=Vinil, cliente=ClienteABC, mult=3, ctr=0px
 * // Saída: "[001]_[004]_Vinil_[X-3]_0px_ClienteABC_[O-12345]_[I-67890]"
 */
export const generateExportFileName = (
	index: number,
	totalPartes: number,
	pedido: PedidoInfo,
	multiplicador: number,
	contraction?: string,
): string => {
	if (!pedido) {
		logger.warn(
			"⚠️ Dados do pedido não fornecidos para geração do nome de exportação",
		);
		return `[${String(index).padStart(3, "0")}]_[${String(totalPartes).padStart(3, "0")}]_arquivo_sem_nome`;
	}

	// Formatar índice com padding (001, 002, etc)
	const indexStr = String(index).padStart(3, "0");
	const totalStr = String(totalPartes).padStart(3, "0");

	// Material (primeira parte para agrupamento visual)
	const materialNome = pedido.material?.nome || "Material";

	// Cliente (sanitizado)
	const sanitizeNomeFantasia = (input: string): string => {
		try {
			let s = String(input || "")
				.normalize("NFD")
				.replace(/[\u0300-\u036f]/g, "");
			s = s.replace(/[<>:"/\\|?*]/g, "-");
			s = s.replace(/[\s_]+/g, "-");
			s = s.replace(/-+/g, "-");
			s = s.replace(/^-+|-+$/g, "");
			return s || "Cliente";
		} catch {
			return "Cliente";
		}
	};
	const nomeFantasia = sanitizeNomeFantasia(
		pedido.cliente?.nome_fantasia || pedido.cliente?.nome || "Cliente",
	);

	// Multiplicador (sempre com prefixo X-)
	const multToken = multiplicador > 1 ? multiplicador : 1;

	// Contração normalizada
	const normalizeContraction = (ctr?: string): string => {
		if (!ctr || ctr === "VAR" || ctr === "VARIÁVEL") return "CV";
		return String(ctr);
	};
	const ctr = normalizeContraction(contraction);

	// IDs do pedido
	const orcIdToken = pedido.orc_id || 0;
	const oitIdToken = pedido.oit_id || 0;

	// Formato final com colchetes: [{index:000}]_[{total:000}]_{material}_[X-{mult}]_{ctr}_{cliente}_[O-{orc_id}]_[I-{oit_id}]
	const fileName = `[${indexStr}]_[${totalStr}]_${materialNome}_[X-${multToken}]_${ctr}_${nomeFantasia}_[O-${orcIdToken}]_[I-${oitIdToken}]`;

	// Sanitizar caracteres inválidos
	const safeName = sanitizeFileName(fileName);

	return safeName;
};

// ================= SAVE TEMP (Centralizado) =================

/**
 * Normaliza string para uso em nomes de pastas (UPPERCASE, sem acentos, sem caracteres especiais)
 *
 * @param value - String a ser normalizada
 * @returns String normalizada para uso em pasta
 *
 * @example
 * normalizeFolderUpper("Impressão DTF Têxtil") → "IMPRESSAO DTF TEXTIL"
 * normalizeFolderUpper("Layout & Design") → "LAYOUT  DESIGN"
 */
export function normalizeFolderUpper(value: string): string {
	try {
		return String(value || "")
			.toUpperCase()
			.normalize("NFD")
			.replace(/[^A-Z0-9\s_]/g, "")
			.replace(/\s+/g, " ")
			.replace(/_+/g, "_")
			.replace(/^_|_$/g, "");
	} catch {
		return "";
	}
}

/**
 * Normaliza nome de categoria para uso em pastas do Save Temp
 * Remove prefixo "IMPRESSÃO " e aplica normalização padrão
 *
 * ⚠️ USO ESPECÍFICO: Apenas para Fluxo 5 (Save Temp)
 *
 * @param categoriaNome - Nome da categoria (ex: "IMPRESSÃO DTF TÊXTIL")
 * @returns Nome normalizado sem prefixo (ex: "DTF TEXTIL")
 *
 * @example
 * normalizeCategoriaSaveTemp("IMPRESSÃO DTF TÊXTIL") → "DTF TEXTIL"
 * normalizeCategoriaSaveTemp("IMPRESSÃO DTF UV") → "DTF UV"
 * normalizeCategoriaSaveTemp("IMPRESSÃO SUBLIMÁTICA") → "SUBLIMATICA"
 * normalizeCategoriaSaveTemp("CAMISETA") → "CAMISETA"
 */
function normalizeCategoriaSaveTemp(categoriaNome: string): string {
	let cleaned = String(categoriaNome || "").trim();

	// Remover prefixo "IMPRESSÃO " (case-insensitive, com ou sem acento)
	// Remove também variações: "IMPRESSÃO", "IMPRESSAO", "IMPRESSÃO", etc.
	cleaned = cleaned.replace(/^impress[aã]o\s+/i, "");

	// Aplicar normalização padrão (remove acentos, uppercase, etc)
	return normalizeFolderUpper(cleaned);
}

/**
 * Mapeamento centralizado de IDs de categoria para nomes de pasta
 *
 * ⚠️ AGORA É DINÂMICO: Busca da API e atualiza automaticamente
 *
 * Usado em:
 * - Gerenciar Arquivos (pré-orçamento)
 * - Preview de destino
 * - Qualquer fluxo que precise do nome padrão de pasta por categoria
 *
 * Cache em memória: O mapeamento é carregado da API na primeira chamada e mantido em cache
 */
let CATEGORIA_PASTA_MAP_CACHE: Record<number, string> = {};

let CATEGORIA_PASTA_MAP_LOADING: Promise<void> | null = null;
let CATEGORIA_PASTA_MAP_LOADED = false;

/**
 * Inicializa o mapeamento de categorias buscando da API
 * @returns Promise que resolve quando o mapeamento estiver carregado
 */
export async function initializeCategoriaPastaMap(): Promise<void> {
	// Se já está carregando, aguardar a promise existente
	if (CATEGORIA_PASTA_MAP_LOADING) {
		return CATEGORIA_PASTA_MAP_LOADING;
	}

	// Se já foi carregado, não precisa carregar novamente
	if (CATEGORIA_PASTA_MAP_LOADED) {
		return Promise.resolve();
	}

	// Criar promise de carregamento
	CATEGORIA_PASTA_MAP_LOADING = (async () => {
		try {
			const categorias = await GerArquivosService.listarProdutoCategorias();

			// Validar se retornou categorias
			if (!Array.isArray(categorias) || categorias.length === 0) {
				throw new Error("API retornou lista vazia de categorias");
			}

			// Popular o cache com os dados da API
			const newMap: Record<number, string> = {};

			for (const categoria of categorias) {
				if (categoria.id && categoria.prod_tipo_descricao) {
					// Normalizar o nome da API para o formato de pasta
					newMap[categoria.id] = normalizeFolderUpper(
						categoria.prod_tipo_descricao,
					);
				}
			}

			// Validar se pelo menos uma categoria foi mapeada
			if (Object.keys(newMap).length === 0) {
				throw new Error(
					"Nenhuma categoria válida encontrada na resposta da API",
				);
			}

			// Atualizar cache APENAS com valores da API (SEM fallback)
			CATEGORIA_PASTA_MAP_CACHE = newMap;

			CATEGORIA_PASTA_MAP_LOADED = true;
		} catch (error) {
			const errorMsg = error instanceof Error ? error.message : String(error);
			try {
				sendToTerminal(
					"❌ [FILE_NAMING] Erro ao carregar mapeamento de categorias",
					{ error: errorMsg },
				);
			} catch {}

			// ❌ SEM FALLBACK: Se falhar, limpar cache e marcar como não carregado
			// Isso força tentativa novamente na próxima chamada
			CATEGORIA_PASTA_MAP_CACHE = {};
			CATEGORIA_PASTA_MAP_LOADED = false;
			CATEGORIA_PASTA_MAP_LOADING = null;

			// Re-throw para que o erro seja propagado
			throw new Error(`Erro ao carregar categorias da API: ${errorMsg}`);
		} finally {
			CATEGORIA_PASTA_MAP_LOADING = null;
		}
	})();

	return CATEGORIA_PASTA_MAP_LOADING;
}

/**
 * Obtém o mapeamento de categorias (carrega da API se necessário)
 * ⚠️ SEM FALLBACK: Se não estiver carregado, retorna objeto vazio (causará erro em mapCategoriaToPastaName)
 *
 * @returns Record<number, string> com o mapeamento de categorias
 * @throws Error se o mapeamento não estiver carregado e não conseguir carregar da API
 */
export function getCategoriaPastaMap(): Readonly<Record<number, string>> {
	// Se não está carregado e não está carregando, iniciar carregamento
	if (!CATEGORIA_PASTA_MAP_LOADED && !CATEGORIA_PASTA_MAP_LOADING) {
		// Iniciar carregamento em background (não aguardar aqui)
		// Mas se falhar, o erro será propagado na próxima tentativa de uso
		initializeCategoriaPastaMap().catch(() => {
			// Erro já foi logado e cache limpo na função
		});
	}

	// Se não está carregado, retornar vazio (causará erro em mapCategoriaToPastaName)
	if (!CATEGORIA_PASTA_MAP_LOADED) {
		return {} as Readonly<Record<number, string>>;
	}

	return CATEGORIA_PASTA_MAP_CACHE as Readonly<Record<number, string>>;
}

/**
 * @deprecated Use getCategoriaPastaMap() para acesso dinâmico
 * Mantido apenas para compatibilidade com código existente
 *
 * Proxy que delega todas as operações para o mapeamento dinâmico carregado da API
 */
export const CATEGORIA_PASTA_MAP: Readonly<Record<number, string>> = new Proxy(
	{} as Record<number, string>,
	{
		get(_target, prop: string | symbol) {
			const map = getCategoriaPastaMap();
			if (typeof prop === "string" || typeof prop === "number") {
				return map[Number(prop)];
			}
			// Para propriedades como Symbol.iterator, etc
			if (prop === Symbol.iterator) {
				return function* () {
					const map = getCategoriaPastaMap();
					for (const key in map) {
						yield [Number(key), map[Number(key)]];
					}
				};
			}
			return undefined;
		},
		ownKeys() {
			const map = getCategoriaPastaMap();
			return Object.keys(map).map(Number).map(String);
		},
		has(_target, prop: string | symbol) {
			const map = getCategoriaPastaMap();
			if (typeof prop === "string" || typeof prop === "number") {
				return Number(prop) in map;
			}
			return false;
		},
		getOwnPropertyDescriptor(_target, prop: string | symbol) {
			const map = getCategoriaPastaMap();
			if (typeof prop === "string" || typeof prop === "number") {
				const key = Number(prop);
				if (key in map) {
					return {
						enumerable: true,
						configurable: true,
						value: map[key],
					};
				}
			}
			return undefined;
		},
	},
) as Readonly<Record<number, string>>;

/**
 * Mapeia ID da categoria para nome de pasta normalizado
 * Usado em "Gerenciar Arquivos" e outros fluxos para criar estrutura de pastas consistente
 *
 * ⚠️ AGORA USA MAPEAMENTO DINÂMICO: Busca da API automaticamente
 *
 * @param categoriaId - ID da categoria (ex: 1, 2, 3, 4...)
 * @param fallbackName - Nome da API para usar como fallback se ID não existir no mapeamento
 * @returns Nome normalizado para uso em pasta
 *
 * @example
 * mapCategoriaToPastaName(1) → "DTF TEXTIL"
 * mapCategoriaToPastaName(99, "Impressão DTF Têxtil") → "IMPRESSAO DTF TEXTIL"
 * mapCategoriaToPastaName(undefined, "Novo Tipo") → "NOVO TIPO"
 */
export function mapCategoriaToPastaName(
	categoriaId?: number,
	fallbackName?: string,
): string {
	// Garantir que o mapeamento está carregado (lazy loading)
	const map = getCategoriaPastaMap();

	// Se tem ID, usar o mapeamento dinâmico
	if (categoriaId && map[categoriaId]) {
		return map[categoriaId];
	}

	// Se não encontrou no mapeamento e tem fallbackName, usar ele (normalizado)
	// Isso permite que mesmo sem mapeamento carregado, funcione se o nome vier da API
	if (fallbackName) {
		return normalizeFolderUpper(fallbackName);
	}

	// ❌ SEM FALLBACK: Se não tem ID nem fallbackName, lançar erro
	const error = categoriaId
		? `Categoria ID ${categoriaId} não encontrada no mapeamento. Verifique se o mapeamento foi carregado da API.`
		: "ID da categoria não fornecido e nenhum nome de fallback disponível.";

	try {
		sendToTerminal("❌ [FILE_NAMING] Categoria não encontrada no mapeamento", {
			categoriaId,
			mapSize: Object.keys(map).length,
		});
	} catch {}

	throw new Error(error);
}

/**
 * Busca regra de salvamento da API por pro_categoria_item
 * @param categoriaId ID da categoria (pro_categoria_item)
 * @returns Regra encontrada
 * @throws Error se não encontrar a regra ou falhar a busca
 */
export async function buscarRegraPorCategoria(categoriaId: number): Promise<{
	diretorio_base_pre: string;
	diretorio_base_gerenciar: string;
	diretorio_base_interno: string;
	diretorio_base_externo: string;
	diretorio_base_exportpng: string;
	diretorio_base_export_prod: string;
	diretorio_nome: string;
	horario_corte: string;
}> {
	if (!categoriaId || categoriaId <= 0) {
		throw new Error(`ID de categoria inválido: ${categoriaId}`);
	}

	try {
		const data = (await api(`/regras-salvamento`, {
			method: "GET",
		})) as {
			success?: boolean;
			data?: Array<{
				pro_categoria_item?: number;
				diretorio_base_pre?: string;
				diretorio_base_gerenciar?: string;
				[key: string]: unknown;
			}>;
		};
		const regras = Array.isArray(data?.data) ? data.data : [];

		const regra = regras.find(
			(r) => Number(r.pro_categoria_item) === categoriaId,
		);

		if (!regra) {
			const errorMsg = `Regra de salvamento não encontrada para categoria ID: ${categoriaId}`;
			try {
				sendToTerminal("❌ [FILE_NAMING] Regra não encontrada", {
					categoriaId,
					totalRegras: regras.length,
				});
			} catch {}
			throw new Error(errorMsg);
		}

		const diretorioBasePre = String(regra.diretorio_base_pre || "").trim();
		const diretorioBaseGerenciar = String(
			regra.diretorio_base_gerenciar || "",
		).trim();

		if (!diretorioBasePre) {
			throw new Error(
				`diretorio_base_pre não encontrado na regra para categoria ID: ${categoriaId}`,
			);
		}

		if (!diretorioBaseGerenciar) {
			throw new Error(
				`diretorio_base_gerenciar não encontrado na regra para categoria ID: ${categoriaId}`,
			);
		}

		// Log removido - regra encontrada é operação rotineira

		const diretorioNome = String(regra.diretorio_nome || "").trim();
		if (!diretorioNome) {
			throw new Error(
				`diretorio_nome não encontrado na regra para categoria ID: ${categoriaId}`,
			);
		}

		return {
			diretorio_base_pre: diretorioBasePre,
			diretorio_base_gerenciar: diretorioBaseGerenciar,
			diretorio_base_interno: String(regra.diretorio_base_interno || ""),
			diretorio_base_externo: String(regra.diretorio_base_externo || ""),
			diretorio_base_exportpng: String(regra.diretorio_base_exportpng || ""),
			diretorio_base_export_prod: String(
				regra.diretorio_base_export_prod || "",
			),
			diretorio_nome: diretorioNome,
			horario_corte: String(regra.horario_corte || "16:00"),
		};
	} catch (error) {
		const errorMsg =
			error instanceof Error
				? error.message
				: `Erro ao buscar regra para categoria ID: ${categoriaId}`;
		try {
			sendToTerminal("❌ [FILE_NAMING] Erro ao buscar regra", {
				categoriaId,
				error: errorMsg,
			});
		} catch {}
		throw error;
	}
}

/**
 * Constrói diretórios/nome para o fluxo SAVE TEMP (sem API)
 * Estrutura:
 * - Interno:  {baseDir}\{YYYY}\{MM-NOME}\{dd.mm.yyyy}\{SERVICO}\
 * - Externo: {baseDir}\{YYYY}\{MM-NOME}\{dd.mm.yyyy}\{SERVICO}\
 * Nome: {baseName}_{QUALIDADE}_{ctr}_{pts}[_{URGENTE|NOTURNA}].cdr
 *
 * @param categoriaId ID da categoria (pro_categoria_item) - obrigatório para buscar regra da API
 */
export async function buildSaveTempPathAndName(params: {
	baseName: string;
	serviceType: string; // legado: mantido no contrato, pasta vem de diretorio_nome da regra
	quality: string;
	totalObjectsWithQuantity: number;
	contraction: string;
	hasMultipleContractions: boolean;
	entrega: "HOJE" | "AMANHÃ" | "NOTURNA" | "URGENTE" | string;
	categoriaId: number; // ⚠️ OBRIGATÓRIO: ID da categoria para buscar diretorio_base_pre da API
}): Promise<{
	directoryInternal: string;
	directoryExternal: string;
	fileName: string;
	fullPathInternal: string;
	fullPathExternal: string;
}> {
	const {
		baseName,
		quality,
		totalObjectsWithQuantity,
		contraction,
		hasMultipleContractions,
		entrega,
		categoriaId,
	} = params;

	// Buscar diretorio_base_pre da API - SEM FALLBACK
	const regra = await buscarRegraPorCategoria(categoriaId);

	const baseDir = regra.diretorio_base_pre;

	if (!baseDir || baseDir.trim() === "") {
		const error = `diretorio_base_pre não encontrado ou vazio para categoria ID: ${categoriaId}`;
		try {
			sendToTerminal("❌ [FILE_NAMING] diretorio_base_pre não encontrado", {
				categoriaId,
			});
		} catch {}
		throw new Error(error);
	}

	// Reutilizar calendário central (formata ano/mês/data e marca urgência/noturna)
	const deliveryMap: Record<string, string> = {
		HOJE: "Hoje",
		AMANHÃ: "Amanhã",
		NOTURNA: "Noturna",
		URGENTE: "URGENTE",
	};
	const deliveryNorm =
		deliveryMap[String(entrega || "HOJE").toUpperCase()] || "Hoje";
	const { dataFormatada, ano, mes } = calculateDestinationDate(
		deliveryNorm,
		regra.horario_corte || "16:00",
	);

	// ✅ Para Save Temp, adicionar ano completo de 4 dígitos: dd.mm.yyyy (com pontos)
	const dataFormatadaComAno = `${dataFormatada.replace(/-/g, ".")}.${ano}`; // Ex: "03.11.2025"

	const serviceFolder = regra.diretorio_nome;
	const qualityNorm = normalizeFolderUpper(quality);
	const contr = hasMultipleContractions
		? CONTRACTION_VARIABLE_FILE
		: contraction || "0px";
	const totalPts = `${totalObjectsWithQuantity}pts`;
	const tag =
		deliveryNorm === "URGENTE" || deliveryNorm === "Noturna"
			? `_${deliveryNorm.toUpperCase()}`
			: "";

	// Usar baseDir da API (mesmo para interno e externo no fluxo Save Temp)
	const baseDirInternal = baseDir;
	const baseDirExternal = baseDir;

	const fileName = `${baseName}_${qualityNorm}_${contr}_${totalPts}_${tag}.cdr`;
	const directoryInternal = `${baseDirInternal}\\${ano}\\${mes}\\${dataFormatadaComAno}\\${serviceFolder}\\`;
	const directoryExternal = `${baseDirExternal}\\${ano}\\${mes}\\${dataFormatadaComAno}\\${serviceFolder}\\`;
	const fullPathInternal = `${directoryInternal}${fileName}`;
	const fullPathExternal = `${directoryExternal}${fileName}`;

	return {
		directoryInternal,
		directoryExternal,
		fileName,
		fullPathInternal,
		fullPathExternal,
	};
}

// ================= PRE-ORÇAMENTO (Gerenciar Arquivos) =================

export type BuildPreOrcamentoDirParams = {
	baseDir?: string; // ex.: D:\\ORCAMENTO
	userId: number | string; // para uid
	userName: string; // pode não ser usado no novo padrão
	date?: Date;
	cliWhatsapp: string; // somente dígitos (normalizado)
	hasCadastro: boolean;
	nomeOpcional?: string; // usado somente quando !hasCadastro (exibição na raiz e no nome)
	clienteId?: number | string; // quando hasCadastro
	clienteNomeFantasia?: string; // quando hasCadastro
	categoriaNome?: string; // {produto-categoria-item} - nome da API (fallback)
	categoriaId?: number; // ID da categoria (1-4) - usado para mapeamento
};

function pad2(n: number): string {
	return String(n).padStart(2, "0");
}
function pad3(n: number | string): string {
	return String(n).padStart(3, "0");
}

/**
 * Gera o diretório de pré-orçamento seguindo o padrão atual do sistema.
 * Mantém compatibilidade: não inclui categoria na árvore (apenas data e telefone).
 *
 * ⚠️ IMPORTANTE: params.baseDir deve ser fornecido (buscar diretorio_base_gerenciar da API antes de chamar).
 * Não há fallback hardcoded - se baseDir não for fornecido, será usado string vazia (causará erro).
 */
export function buildPreOrcamentoDirFromParams(
	params: BuildPreOrcamentoDirParams,
): string {
	if (!params.baseDir || params.baseDir.trim() === "") {
		throw new Error(
			"baseDir é obrigatório. Busque diretorio_base_gerenciar da API antes de chamar esta função.",
		);
	}

	const base = params.baseDir;
	const d = params.date || new Date();
	const yyyy = d.getFullYear();
	const mmNum = d.getMonth() + 1;
	const mm = pad2(mmNum);
	const monthUpper = [
		"JANEIRO",
		"FEVEREIRO",
		"MARÇO",
		"ABRIL",
		"MAIO",
		"JUNHO",
		"JULHO",
		"AGOSTO",
		"SETEMBRO",
		"OUTUBRO",
		"NOVEMBRO",
		"DEZEMBRO",
	][mmNum - 1];
	const mmLabel = `${mm}-${monthUpper}`;
	const dd = pad2(d.getDate());
	const dayLabel = `${dd}-${mm}`; // v3.0: dd-mm (SEM ano)
	const sanitize = (s?: string) =>
		(s || "")
			.normalize("NFD")
			.replace(/[\u0300-\u036f]/g, "")
			.replace(/[^a-zA-Z0-9\s]+/g, " ")
			.replace(/\s+/g, "_")
			.replace(/_+/g, "_")
			.replace(/^_|_$/g, "");

	// Raiz conforme especificação
	// Cliente COM cadastro: {cli_id}_{cli_nome_fantasia}
	// Cliente SEM cadastro: SEM-CADASTRO\{whatsapp}_{nome_opcional}
	const raiz = params.hasCadastro
		? `${String(params.clienteId ?? "")}_${sanitize(
				params.clienteNomeFantasia,
			)}`
		: `SEM-CADASTRO\\${params.cliWhatsapp}_${
				params.nomeOpcional ? sanitize(params.nomeOpcional) : ""
			}`;

	// Mapear categoria: prioriza ID, fallback para nome da API
	const cat = mapCategoriaToPastaName(params.categoriaId, params.categoriaNome);

	// ✅ Estrutura atualizada: {baseDir}\CLIENTES\{raiz}\{ano}\{mes}\{dd-mm}\{categoria}
	return `${base}\\CLIENTES\\${raiz}\\${yyyy}\\${mmLabel}\\${dayLabel}\\${cat}`;
}

/**
 * Gera nome de arquivo de pré-orçamento v3.0
 * - Cliente SEM cadastro: {whatsapp}_{nomeOpcional}_{yyyy}{MM}{dd}_{hash}.cdr
 * - Cliente COM cadastro: {clienteNomeFantasia}_{yyyy}{MM}{dd}_{hash}.cdr
 * @param cliWhatsappDigits - Dígitos do WhatsApp
 * @param userId - ID do usuário (não usado em v3.0)
 * @param date - Data do arquivo
 * @param nomeOpcional - Nome opcional para clientes sem cadastro
 * @param clienteNomeFantasia - Nome fantasia para clientes com cadastro
 * @param hasCadastro - Se o cliente tem cadastro
 * @returns Nome do arquivo com extensão .cdr
 */
export function generatePreOrcFileNameFromParams(
	cliWhatsappDigits: string,
	userId: number | string,
	date: Date = new Date(),
	nomeOpcional?: string,
	clienteNomeFantasia?: string,
	hasCadastro?: boolean,
): string {
	const yyyy = date.getFullYear();
	const mm = pad2(date.getMonth() + 1);
	const dd = pad2(date.getDate());
	const hash = generateFourDigitHash();

	const sanitizeInline = (s?: string) =>
		s
			? s
					.normalize("NFD")
					.replace(/[\u0300-\u036f]/g, "")
					.replace(/\s+/g, "")
			: "";

	if (hasCadastro) {
		// COM cadastro: {clienteNomeFantasia}_{yyyy}{MM}{dd}_{hash}.cdr
		const namePart = sanitizeInline(clienteNomeFantasia);
		return `${namePart}_${yyyy}${mm}${dd}_${hash}.cdr`;
	} else {
		// SEM cadastro: {whatsapp}_{nomeOpcional}_{yyyy}{MM}{dd}_{hash}.cdr
		const cli = String(cliWhatsappDigits || "").replace(/\D/g, "");
		const namePart = sanitizeInline(nomeOpcional);
		return `${cli}_${namePart}_${yyyy}${mm}${dd}_${hash}.cdr`;
	}
}

/** Helper: retorna dir, fileName e fullPath para pré-orçamento */
export function generatePreOrcPathAndName(params: BuildPreOrcamentoDirParams): {
	dir: string;
	fileName: string;
	fullPath: string;
} {
	const dir = buildPreOrcamentoDirFromParams(params);
	const fileName = generatePreOrcFileNameFromParams(
		params.cliWhatsapp,
		params.userId,
		params.date || new Date(),
		params.nomeOpcional,
		params.clienteNomeFantasia,
		params.hasCadastro,
	);
	return { dir, fileName, fullPath: `${dir}\\${fileName}` };
}
